import { useState, useEffect, useCallback } from 'react';
import {
  Wifi, WifiOff, RefreshCw, Users, Calendar, CheckCircle,
  AlertCircle, Loader2, Download, ChevronDown, ChevronUp, Clock, Search
} from 'lucide-react';
import { erpAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];

export default function ErpSync() {
  const { isAdmin, isSuperAdmin } = useAuth();
  const canSync = isAdmin || isSuperAdmin;

  const [status,       setStatus]       = useState(null);   // null | {connected, message, employeeCount}
  const [statusLoading,setStatusLoading]= useState(false);
  const [erpEmployees, setErpEmployees] = useState([]);
  const [empLoading,   setEmpLoading]   = useState(false);
  const [showEmpList,  setShowEmpList]  = useState(false);

  const today = new Date();
  const [syncMonth, setSyncMonth] = useState(today.getMonth() + 1);
  const [syncYear,  setSyncYear]  = useState(today.getFullYear());
  const [syncing,   setSyncing]   = useState(false);
  const [syncResult,setSyncResult]= useState(null);

  const [empSyncing,   setEmpSyncing]   = useState(false);
  const [empSyncResult,setEmpSyncResult]= useState(null);

  const [diagnosing,   setDiagnosing]   = useState(false);
  const [diagResult,   setDiagResult]   = useState(null);   // { results, best, bestLabel }
  const [showDiag,     setShowDiag]     = useState(false);

  const [toast, setToast] = useState(null);
  const showToast = (msg, type='success') => { setToast({msg, type}); setTimeout(()=>setToast(null), 4000); };

  // ── Test ERP connection ────────────────────────────────────────────────────
  const testConnection = useCallback(async () => {
    setStatusLoading(true); setStatus(null);
    try {
      const r = await erpAPI.getStatus();
      setStatus(r.data);
    } catch(e) {
      setStatus({ connected: false, error: e.response?.data?.error || e.message });
    } finally { setStatusLoading(false); }
  }, []);

  // Auto-test on mount
  useEffect(() => { testConnection(); }, [testConnection]);

  // ── Load ERP employee list ─────────────────────────────────────────────────
  const loadErpEmployees = async () => {
    setEmpLoading(true);
    try {
      const r = await erpAPI.getEmployees();
      setErpEmployees(r.data.employees || []);
      setShowEmpList(true);
    } catch(e) { showToast(e.response?.data?.error || 'Failed to load', 'error'); }
    finally { setEmpLoading(false); }
  };

  // ── Sync employee codes ────────────────────────────────────────────────────
  const handleSyncEmployees = async () => {
    setEmpSyncing(true); setEmpSyncResult(null);
    try {
      const r = await erpAPI.syncEmployees();
      setEmpSyncResult(r.data);
      showToast(r.data.message);
    } catch(e) { showToast(e.response?.data?.error || 'Sync failed', 'error'); }
    finally { setEmpSyncing(false); }
  };

  // ── Diagnose ERP employee endpoints ───────────────────────────────────────
  const handleDiagnose = async () => {
    setDiagnosing(true); setDiagResult(null); setShowDiag(true);
    try {
      const r = await erpAPI.diagnoseEmployees();
      setDiagResult(r.data);
    } catch(e) { showToast(e.response?.data?.error || 'Diagnose failed', 'error'); setShowDiag(false); }
    finally { setDiagnosing(false); }
  };

  // ── Sync attendance ────────────────────────────────────────────────────────
  const handleSyncAttendance = async () => {
    setSyncing(true); setSyncResult(null);
    try {
      const r = await erpAPI.syncAttendance({ month: syncMonth, year: syncYear });
      setSyncResult(r.data);
      showToast(r.data.message);
    } catch(e) {
      const err = e.response?.data?.error || e.message;
      setSyncResult({ error: err });
      showToast(err, 'error');
    } finally { setSyncing(false); }
  };

  const years = [today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1];

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl shadow-lg text-sm font-medium flex items-center gap-2
          ${toast.type === 'error' ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-emerald-50 border border-emerald-200 text-emerald-700'}`}>
          {toast.type === 'error' ? <AlertCircle size={16}/> : <CheckCircle size={16}/>}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-slate-800">Edunext ERP Integration</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Sync staff attendance directly from Edunext ERP — no biometric device required.
          ERP: <code className="bg-slate-100 px-1.5 rounded text-xs">tksj.edunexttechnologies.com</code>
        </p>
      </div>

      {/* ── Connection Status ── */}
      <div className="glass-card p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
            <Wifi size={16} className="text-primary-600"/> ERP Connection Status
          </h2>
          <button onClick={testConnection} disabled={statusLoading}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-60">
            {statusLoading ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>}
            Test Connection
          </button>
        </div>

        {statusLoading && (
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Loader2 size={14} className="animate-spin"/> Connecting to Edunext ERP…
          </div>
        )}

        {status && !statusLoading && (
          status.connected ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-2">
              <p className="text-sm font-semibold text-emerald-700 flex items-center gap-1.5">
                <CheckCircle size={14}/> Connected to Edunext ERP
              </p>
              <p className="text-xs text-emerald-600">{status.message}</p>
              <div className="flex gap-4 text-xs text-emerald-600">
                <span>👥 <strong>{status.employeeCount}</strong> employees in ERP</span>
              </div>
            </div>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-2">
              <WifiOff size={14} className="text-red-500 flex-shrink-0 mt-0.5"/>
              <div>
                <p className="text-sm font-semibold text-red-700">Cannot connect to ERP</p>
                <p className="text-xs text-red-600 mt-0.5">{status.error}</p>
              </div>
            </div>
          )
        )}
      </div>

      {/* ── Employee Sync ── */}
      <div className="glass-card p-5 space-y-4">
        <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <Users size={16} className="text-primary-600"/> Employee Sync
        </h2>
        <p className="text-xs text-slate-500">
          Sync all employee data from ERP — inserts new employees found in ERP and updates
          profile fields (code, email, designation, department, joining date) for existing staff.
        </p>

        <div className="flex gap-2 flex-wrap">
          <button onClick={handleSyncEmployees} disabled={empSyncing || !canSync}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60 transition-colors">
            {empSyncing ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>}
            {empSyncing ? 'Syncing…' : 'Sync All Employee Data'}
          </button>
          <button onClick={loadErpEmployees} disabled={empLoading}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-slate-200 hover:bg-slate-50 rounded-lg disabled:opacity-60 transition-colors">
            {empLoading ? <Loader2 size={14} className="animate-spin"/> : <Users size={14}/>}
            {showEmpList ? 'Refresh' : 'View ERP Employees'}
            {showEmpList ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
          </button>
          <button onClick={handleDiagnose} disabled={diagnosing || !canSync}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-amber-200 text-amber-700 hover:bg-amber-50 rounded-lg disabled:opacity-60 transition-colors">
            {diagnosing ? <Loader2 size={14} className="animate-spin"/> : <Search size={14}/>}
            {diagnosing ? 'Probing…' : 'Diagnose ERP Fields'}
          </button>
        </div>

        {empSyncResult && (
          <div className={`rounded-xl p-3 text-xs border ${
            (empSyncResult.inserted > 0 || empSyncResult.updated > 0)
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
              : 'bg-amber-50 border-amber-200 text-amber-700'
          }`}>
            <p className="font-medium mb-2">{empSyncResult.message}</p>
            <div className="grid grid-cols-4 gap-2">
              {[
                { label: 'Total in ERP',  value: empSyncResult.total    ?? 0 },
                { label: 'Inserted',      value: empSyncResult.inserted ?? 0 },
                { label: 'Updated',       value: empSyncResult.updated  ?? 0 },
                { label: 'Skipped',       value: empSyncResult.skipped  ?? 0 },
              ].map(({ label, value }) => (
                <div key={label} className="bg-white/70 rounded-lg p-2 text-center">
                  <div className="font-bold text-base">{value}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{label}</div>
                </div>
              ))}
            </div>
            {empSyncResult.unmatched?.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer">Errors / unmatched ({empSyncResult.unmatched.length})</summary>
                <div className="mt-1 space-y-0.5">
                  {empSyncResult.unmatched.map((e, i) => (
                    <div key={i} className="font-mono">{e.code || '—'} · {e.name || '—'}{e.reason ? ` — ${e.reason}` : ''}</div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}

        {/* Diagnose results */}
        {showDiag && (
          <div className="border border-amber-200 rounded-xl overflow-hidden text-xs">
            <div className="bg-amber-50 px-4 py-2 flex items-center justify-between">
              <span className="font-semibold text-amber-800">
                {diagnosing ? 'Probing 15 endpoints…' : `Diagnose complete — best: ${diagResult?.bestLabel || '…'}`}
              </span>
              <button onClick={() => setShowDiag(false)} className="text-amber-400 hover:text-amber-700">✕</button>
            </div>
            {diagnosing && (
              <div className="px-4 py-3 flex items-center gap-2 text-slate-400">
                <Loader2 size={13} className="animate-spin"/> Testing endpoints, please wait…
              </div>
            )}
            {diagResult && (
              <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                {diagResult.results.map(r => (
                  <div key={r.id} className={`px-4 py-2 ${r.rowCount > 0 ? 'bg-emerald-50' : ''}`}>
                    <div className="flex items-center gap-2">
                      <span className={`font-mono font-bold text-[11px] w-5 text-center rounded ${r.rowCount > 0 ? 'text-emerald-700' : 'text-slate-400'}`}>{r.id}</span>
                      <span className={`flex-1 ${r.rowCount > 0 ? 'text-emerald-800 font-medium' : 'text-slate-400'}`}>{r.label}</span>
                      <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${r.rowCount > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
                        {r.error ? 'error' : r.rowCount > 0 ? `${r.rowCount} rows` : r.apiSuccess ? 'empty' : `HTTP ${r.httpStatus}`}
                      </span>
                    </div>
                    {r.rowCount > 0 && r.fields.length > 0 && (
                      <div className="mt-1 ml-7 text-[10px] text-slate-500">
                        <span className="font-semibold text-slate-600">Fields: </span>
                        {r.fields.join(', ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ERP Employee List */}
        {showEmpList && erpEmployees.length > 0 && (
          <div className="border border-slate-100 rounded-xl overflow-hidden">
            <div className="bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-600 flex justify-between">
              <span>ERP Employees ({erpEmployees.length})</span>
              <button onClick={()=>setShowEmpList(false)} className="text-slate-400 hover:text-slate-600">Hide</button>
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-slate-50">
              {erpEmployees.map(e => (
                <div key={e.id} className="px-4 py-2 flex items-center justify-between text-xs">
                  <span className="text-slate-700">{e.name}</span>
                  <span className="font-mono text-slate-400">{e.code}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Attendance Sync ── */}
      <div className="glass-card p-5 space-y-4">
        <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <Calendar size={16} className="text-primary-600"/> Attendance Sync
        </h2>
        <p className="text-xs text-slate-500">
          Pull monthly attendance data from Edunext ERP directly into the dashboard.
          Select month and year, then click Sync.
        </p>

        {/* Month/Year picker */}
        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Month</label>
            <select value={syncMonth} onChange={e=>setSyncMonth(parseInt(e.target.value))}
              className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500">
              {MONTHS.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Year</label>
            <select value={syncYear} onChange={e=>setSyncYear(parseInt(e.target.value))}
              className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500">
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <button onClick={handleSyncAttendance} disabled={syncing || !canSync}
            className="flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60 transition-colors shadow-sm">
            {syncing ? <Loader2 size={14} className="animate-spin"/> : <Download size={14}/>}
            {syncing ? 'Syncing from ERP…' : `Sync ${MONTHS[syncMonth-1]} ${syncYear}`}
          </button>
        </div>

        {/* Sync result */}
        {syncResult && (
          <div className={`rounded-xl p-4 border text-sm space-y-1.5 ${
            syncResult.error ? 'bg-red-50 border-red-200 text-red-700' :
            syncResult.synced > 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
            'bg-amber-50 border-amber-200 text-amber-700'
          }`}>
            {syncResult.error ? (
              <p className="font-medium flex items-center gap-1.5">
                <AlertCircle size={14}/> {syncResult.error}
              </p>
            ) : (
              <>
                <p className="font-semibold flex items-center gap-1.5">
                  <CheckCircle size={14}/> {syncResult.message}
                </p>
                <div className="grid grid-cols-3 gap-3 mt-2 text-xs">
                  <div className="bg-white/70 rounded-lg p-2 text-center">
                    <div className="font-bold text-lg">{syncResult.synced}</div>
                    <div className="text-slate-500">Records synced</div>
                  </div>
                  <div className="bg-white/70 rounded-lg p-2 text-center">
                    <div className="font-bold text-lg">{syncResult.unmatched}</div>
                    <div className="text-slate-500">Unmatched</div>
                  </div>
                  <div className="bg-white/70 rounded-lg p-2 text-center">
                    <div className="font-bold text-lg">{syncResult.total}</div>
                    <div className="text-slate-500">Total ERP rows</div>
                  </div>
                </div>
                {syncResult.unmatched > 0 && (
                  <p className="text-xs mt-1 opacity-80">
                    {syncResult.unmatched} employees couldn't be matched — use "Sync Employee Codes" first to fix mappings.
                  </p>
                )}
                {syncResult.errors?.length > 0 && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-xs">Errors ({syncResult.errors.length})</summary>
                    <div className="text-[11px] mt-1 space-y-0.5">
                      {syncResult.errors.map((e,i) => <div key={i}>{e}</div>)}
                    </div>
                  </details>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* ── How it works ── */}
      <div className="glass-card p-5 space-y-3">
        <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <Clock size={16} className="text-slate-500"/> How It Works
        </h2>
        <ol className="space-y-2 text-xs text-slate-500">
          <li className="flex gap-2">
            <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0">1</span>
            <span><strong className="text-slate-700">Dashboard logs in</strong> to Edunext ERP using the Edunext admin credentials.</span>
          </li>
          <li className="flex gap-2">
            <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0">2</span>
            <span><strong className="text-slate-700">Fetches employee data</strong> from the ERP's REST API — full profile (name, code, email, designation, department) for the employee sync, and monthly attendance records for the attendance sync.</span>
          </li>
          <li className="flex gap-2">
            <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0">3</span>
            <span><strong className="text-slate-700">Matches employees</strong> by employee code (TKSJ-E077 etc.) and saves attendance to the dashboard.</span>
          </li>
          <li className="flex gap-2">
            <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0">4</span>
            <span><strong className="text-slate-700">View in Attendance module</strong> — synced records appear immediately in the Employee Attendance section.</span>
          </li>
        </ol>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-700 mt-2">
          <strong>Note:</strong> The biometric device is no longer needed if all attendance is entered in Edunext ERP.
          You can sync attendance here daily or whenever needed.
        </div>
      </div>
    </div>
  );
}
